// src/hooks/useSessionMap.js
import { useRef } from 'react';
import { initializeLeafletMap, addRecenterControl, addMapTypeControl, toggleMapType } from '../mapManager.js';
import { renderVirtualSessionToMap, updatePath } from '../mapHelper.js';

export const useSessionMap = (virtualSessionRef, stateRef) => {
    const mapRef = useRef(null);
    const responderMarkersRef = useRef({});
    const layersRef = useRef({
        routeLayer: null,
        corridorLayer: null,
        currentPositionMarker: null,
        pathLine: null,
        pathSegments: [],
        pathMarkers: [],
        responderRoutes: {}
    });

    const initMap = (containerId) => {
        const container = document.getElementById(containerId);
        const mapDiv = document.getElementById('map');

        if (!container || !mapDiv) {
            console.error(`[MAP] Conteneur #${containerId} ou #map introuvable`);
            return false;
        }

        // Déplace l'élément DOM de la carte dans le conteneur actif
        if (mapDiv.parentElement !== container) {
            mapDiv.style.position = 'relative';
            mapDiv.style.top = '0';
            mapDiv.style.left = '0';
            container.appendChild(mapDiv);
        }

        if (mapRef.current) return true;

        mapRef.current = initializeLeafletMap(mapDiv);
        if (mapRef.current) {
            addRecenterControl(mapRef.current, () => {
                const loc = virtualSessionRef.current.currentLocation;
                if (loc) {
                    const lat = loc.latitude ?? loc.lat;
                    const lng = loc.longitude ?? loc.lng;
                    if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
                        mapRef.current.setView([lat, lng], 16);
                    }
                }
            });
            addMapTypeControl(mapRef.current, (showSatellite) => {
                toggleMapType(mapRef.current, showSatellite);
            });
            return true;
        }
        return false;
    };

    /**
     * Mise à jour globale pour le mode direct ou rejeu complet.
     */
    const updateMapObjects = (customSession = null, customPositions = null) => {
        if (!mapRef.current) return;
        const session = customSession || virtualSessionRef.current;
        renderVirtualSessionToMap(session, mapRef.current, layersRef.current, responderMarkersRef);

        const posEvents = customPositions || (stateRef.current.allEvents || []).filter(ev => ev.eventType === 'NEWPOSITION');
        updatePath(posEvents, mapRef.current, layersRef.current);
    };

    /**
     * Mise à jour instantanée avec positions et session pré-calculées (O(1)).
     */
    const updateMapObjectsAtSnapshot = (snapshot, positions, shouldCenter = false) => {
        if (!mapRef.current || !snapshot) return;

        renderVirtualSessionToMap(snapshot, mapRef.current, layersRef.current, responderMarkersRef);
        updatePath(positions, mapRef.current, layersRef.current);

        if (shouldCenter && snapshot.currentLocation) {
            const lat = Number(snapshot.currentLocation.latitude ?? snapshot.currentLocation.lat);
            const lng = Number(snapshot.currentLocation.longitude ?? snapshot.currentLocation.lng);
            if (!isNaN(lat) && !isNaN(lng)) {
                mapRef.current.setView([lat, lng], 16, { animate: false });
            }
        }
    };

    const invalidateMapSize = () => {
        if (mapRef.current) {
            setTimeout(() => {
                if (mapRef.current) {
                    mapRef.current.invalidateSize();
                    const loc = virtualSessionRef.current.currentLocation;
                    if (loc) {
                        const lat = Number(loc.latitude ?? loc.lat);
                        const lng = Number(loc.longitude ?? loc.lng);
                        if (!isNaN(lat) && !isNaN(lng)) {
                            mapRef.current.setView([lat, lng], 16, { animate: true });
                        }
                    }
                    console.log("[MAP] 📏 Taille de la carte recalculée et recentrée");
                }
            }, 300);
        }
    };

    const cleanupMap = () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
        }
        responderMarkersRef.current = {};
        layersRef.current = {
            routeLayer: null,
            corridorLayer: null,
            currentPositionMarker: null,
            pathLine: null,
            pathSegments: [],
            pathMarkers: [],
            responderRoutes: {}
        };
    };

    return {
        mapRef,
        layersRef,
        initMap,
        updateMapObjects,
        updateMapObjectsAtSnapshot,
        invalidateMapSize,
        cleanupMap
    };
};
